<template>
  <step>
    <template #header
      ><span>Your Rate & Term Options</span><br />
      <span>
        {{ promoDisclaimerText }}
      </span></template
    >

    <p>
      Below are your options for rates with {{ formattedTerms }}.
      <span class="bold underline">Please select one.</span>
    </p>
    <div class="info-wrapper">
      <div class="info-header italic">What's the difference between a fixed and variable rate?</div>
      <p class="info-content">
        A fixed rate means that your interest rate will be the same for the life of your loan. A
        variable rate will fluctuate over time based on the current {{ indexTypeName }} index rate.
        A variable interest rate will be the index percent plus a margin percent, which is set based
        on your credit score.
        <a
          class="link"
          href="https://www.studentchoice.org/faq-items/what-is-the-difference-between-a-fixed-and-variable-rate/"
          target="_blank"
          >Click here</a
        >
        to read more.
      </p>
    </div>
    <div class="loan-options-wrapper">
      <RefiProgramLoanOption
        v-for="ratePlan in availableRatePlans"
        :key="ratePlan"
        :rate-plan-option="ratePlan"
        :term="ratePlan.year"
        :class="{ active: selectedRateTerm === ratePlan?.year }"
      />
    </div>
  </step>
</template>
<script setup lang="ts">
import { defineEmits, computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import Step from "@/components/shared/Step.vue";
import RefiProgramLoanOption from "@/components/refi-loan-configuration/RefiProgramLoanOption.vue";
import { RefiRatePlanViewModel } from "@/models/refi";

const store = useStore();
const emit = defineEmits(["tileClicked"]);

const loanConfiguration = computed(() => store.state.appData.application.loanConfiguration);
const indexTypeName = computed(() => store?.state?.lenderData?.indexTypeName);
const selectedRateTerm = computed(() => store.state.refiLoanConfigurationModule.selectedRateTerm);
const availableRatePlans = computed(
  () => store.getters["refiLoanConfigurationModule/AvailableRatePlans"],
);
const terms = computed(() => getRepaymentTerms());
const formattedTerms = computed(() => {
  if (terms.value.length === 1) {
    return terms.value
      ?.map((n: any) => {
        return `${n}-year term`;
      })
      .join(" ");
  }

  if (terms.value.length === 2) {
    return terms.value
      ?.map((n: any, index: any) => {
        return index === terms.value.length - 1 ? `or ${n}-year terms` : `${n}-year`;
      })
      .join(" ");
  }

  return terms.value?.map((n: any, index: any) => {
    return index === terms.value.length - 1 ? `or ${n}-year terms` : `${n}-year,`;
  }).join(" ");
});

const promoDisclaimerText = computed(
  () => store.state.refiLoanConfigurationModule.programPromoDisclaimerText,
);

function getRepaymentTerms() {
  const availableTerms = availableRatePlans.value?.map((ratePlan: RefiRatePlanViewModel) => {
    return ratePlan.year;
  });
  return availableTerms?.sort((a: any, b: any) => a - b) || [];
}

async function setPreviouslySelectedRatePlanId() {
  // verify that the rate plans are populated
  if (availableRatePlans.value?.length === 0) {
    await store.dispatch("refiLoanConfigurationModule/getRefiRatePlans");
  }

  // Check if loan config has been set
  if (loanConfiguration.value && loanConfiguration.value?.ratePlanId) {
    store.dispatch(
      "refiLoanConfigurationModule/setSelectedRatePlanId",
      loanConfiguration.value?.ratePlanId,
    );
  }
}

onMounted(() => {
  setPreviouslySelectedRatePlanId();
});
</script>
<style scoped>
.bold.underline {
  text-decoration: underline;
  font-weight: 700;
}
.link {
  font-family: var(--body-font);
  color: #2e3192;
  font-weight: 400;
  line-height: 21px;
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;
}
.info-wrapper .info-header {
  font-family: var(--body-font);
  font-weight: 400;
  font-size: 1rem;
  line-height: 20px;
}
.info-wrapper .info-content {
  font-family: var(--body-font);
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 20px;
  margin-top: 0;
}
</style>
